/**
 * What to call the intake form, in a firm's own words or in ours (steward-app#1262,
 * house default changed to "financial snapshot" per Matt's 2026-09-19 decision).
 *
 * ## The defect this module retired, and what changed since
 *
 * "Financial Snapshot" used to be **RIPL's** name for its own questionnaire, reused
 * house-wide by accident. docs/25 fixed the naming rule at the identifier level —
 * *"'Financial Snapshot' is UI copy only; identifiers say intake"* — and the identifiers
 * held it: every route, `typeCode`, handler and queue field still says `intake`. The COPY
 * did not, at first: the phrase had reached ~10 files (the public wizard's heading and
 * document title, the confirmation page, the advisor portal shown to every firm, the footer
 * and title of the PDF a firm hands its own client) as one firm's brand wearing a house
 * default. This module retired that by making the fallback plain ("intake form") instead of
 * RIPL's own words.
 *
 * Matt has since decided the HOUSE word itself should be "financial snapshot" — plain,
 * descriptive of what the form actually collects, and not any one firm's brand (RIPL's own
 * `intakeLabel` is a proper noun of theirs, "Financial Snapshot", capitalised as a name; the
 * house default below is a common noun, lower-case, and is not that firm's string even
 * though the words now coincide). The defect this file exists to retire was never the
 * WORDS "financial snapshot" — it was one firm's config-free brand reaching every other
 * firm by omission. That is still true here: a firm with no `intakeLabel` gets the house
 * value below, and a firm WITH one still gets its own word, unchanged.
 *
 * ## Why a function and not a `??` at each call site
 *
 * ⚠️ **The fallback is the whole point, and it must be house-neutral.** Ten call sites each
 * writing `firm.intakeLabel?.singular ?? 'Financial Snapshot'` would reproduce the ORIGINAL
 * bug exactly: the next firm inherits RIPL's brand by omission, which is that defect wearing
 * a config file. One function means the neutral default is asserted once
 * (`intakeNoun.test.ts`) and cannot be re-introduced by a call site that copies its
 * neighbour.
 *
 * ## Why the fallback WORD lives in `@steward-app/steward-types`, not here
 *
 * `HOUSE_INTAKE_NOUN` (re-exported below) is defined once in the shared package, not here
 * and not again in `services/steward-app-api/src/services/claimEmail.ts`, which needs the
 * identical word for the façade's claim mail and cannot import from `src/` (steward-app#319:
 * the SPA and the façade are separate deploy artifacts, with no dependency edge between
 * them — the reverse of the shared package edge each already has). Two hand-kept copies of
 * a user-facing word is exactly the drift a shared package exists to prevent, and it was
 * tried here first: see `packages/steward-types/src/advisorFirmDirectory.ts`'s docblock on
 * {@link HOUSE_INTAKE_NOUN} for the fuller argument.
 *
 * ## Casing
 *
 * Returns the string exactly as stored. A firm's label is a proper noun of theirs
 * ("Financial Snapshot"), the fallback is a common noun ("financial snapshot"), and those
 * want different capitalisation in the same sentence position. Callers that need a
 * sentence-leading capital use {@link capitalizedIntakeNoun}; nothing here guesses, because
 * title-casing a firm's own name is how "Financial Snapshot" becomes "Financial snapshot" on
 * that firm's PDF.
 */

import { HOUSE_INTAKE_NOUN, type AdvisorFirmIntakeLabel } from '@steward-app/steward-types';

/**
 * Re-exported so every existing call site and test in this file's own package can keep
 * importing it from here — the ONE definition lives in `@steward-app/steward-types` (see
 * this file's header, "Why the fallback WORD lives in `@steward-app/steward-types`"), and
 * this is a re-export, not a second copy. Tests can still assert on it by reference rather
 * than by retyping the literal — a test that hard-codes 'financial snapshot' passes just as
 * happily against a default someone quietly changed back to a firm's brand.
 */
export { HOUSE_INTAKE_NOUN };

/** Just enough of a firm for this module. Keeps callers from passing a whole hydrated firm. */
export interface IntakeNounSource {
  /**
   * Typed as the validated shape for callers that have one, but {@link intakeNoun} does not
   * TRUST it: on the advisor path the value arrives through an unchecked cast of a response
   * body, so the function re-checks each field it reads. See the ⚠️ there.
   */
  readonly intakeLabel?: AdvisorFirmIntakeLabel;
}

/**
 * The firm's word for its intake form, or the house-neutral one.
 *
 * `firm` is optional and nullable on purpose: the public wizard renders before the firm has
 * resolved, and "we do not know the firm yet" must produce neutral copy rather than a
 * flicker of one firm's brand or an empty heading.
 */
export function intakeNoun(
  firm: IntakeNounSource | null | undefined,
  form: 'singular' | 'plural' = 'singular',
): string {
  const label = firm?.intakeLabel as { singular?: unknown; plural?: unknown } | undefined;
  const value = label?.[form];
  // ⚠️ **Guarded per-field, because on the advisor path this is UNVALIDATED WIRE JSON.**
  //
  // An earlier version of this function declined the check, on the grounds that
  // `AdvisorFirmIntakeLabelSchema` already refuses an empty or half-built label. That is
  // true of the WRITE side and false of this consumer, and the difference is a crash:
  // `useAdvisorFirm` reaches this value through `get<AdvisorFirmsResponse>()`, which is a
  // CAST and not a schema parse (`firmChoice` on that same payload is defended with
  // `Array.isArray` for exactly this reason). A response carrying
  // `intakeLabel: { singular: 'Snapshot' }` would make `capitalizedIntakeNoun(firm,
  // 'plural')` call `.charAt(0)` on `undefined` and take the whole AdvisorOverview render
  // down. Found by the code review of steward-app#1264.
  //
  // The trim also matters and the schema does NOT cover it: `logSafeText` is `.min(1)` with
  // no trim, so `'   '` is a schema-valid label that would render "No     yet."
  if (typeof value !== 'string' || value.trim() === '') return HOUSE_INTAKE_NOUN[form];
  return value;
}

/**
 * {@link intakeNoun} with the first letter upper-cased, for a sentence-leading or
 * heading position.
 *
 * Upper-cases the FIRST CHARACTER ONLY and leaves the rest of the string untouched. Not
 * title case, and not `toLowerCase()` first: "Financial Snapshot" must survive this
 * unchanged, and a naive title-caser would also mangle a firm whose name contains an
 * intentional lowercase word or an acronym.
 */
export function capitalizedIntakeNoun(
  firm: IntakeNounSource | null | undefined,
  form: 'singular' | 'plural' = 'singular',
): string {
  const noun = intakeNoun(firm, form);
  return noun.charAt(0).toUpperCase() + noun.slice(1);
}
